import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Skeleton } from "./ui/skeleton";
import { ChevronDown, Info, Lock, ShieldCheck } from "lucide-react";
import api, { formatEUR, formatCrypto } from "../lib/api";
import AssetSelector from "./AssetSelector";
import AnimatedNumber from "./AnimatedNumber";
import { useAuth } from "../lib/auth";
import AuthGate from "./AuthGate";
import { toast } from "sonner";

const QUICK = [100, 250, 500, 1000];

const cryptoFormat = (v) => {
  if (!v) return "0.00";
  const abs = Math.abs(v);
  const d = abs < 1 ? 6 : abs < 100 ? 4 : 2;
  return Number(v).toLocaleString("en-IE", { maximumFractionDigits: d });
};

export default function QuoteScreen({ onContinue, initialAsset, initialAmount }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prices, setPrices] = useState([]);
  const [asset, setAsset] = useState(initialAsset || "USDC");
  const [network, setNetwork] = useState("Ethereum (ERC-20)");
  const [amount, setAmount] = useState(initialAmount || 500);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feeOpen, setFeeOpen] = useState(false);
  const [payment, setPayment] = useState("sepa");
  const [countdown, setCountdown] = useState(60);
  const [authOpen, setAuthOpen] = useState(false);
  const debounceRef = useRef();

  useEffect(() => {
    api.get("/prices").then((r) => setPrices(r.data.prices || []));
    const id = setInterval(() => {
      api.get("/prices").then((r) => setPrices(r.data.prices || []));
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const networks = prices.find((p) => p.symbol === asset)?.networks || [];
  useEffect(() => {
    if (networks.length && !networks.includes(network)) setNetwork(networks[0]);
  }, [asset, networks, network]);

  // Debounced quote fetch
  useEffect(() => {
    if (!amount || amount < 20 || !asset || !network) { setQuote(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.post("/v1/quotes", { fiat_amount: Number(amount), asset, network });
        setQuote(r.data);
        setCountdown(60);
      } catch (e) {
        setQuote(null);
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [amount, asset, network]);

  useEffect(() => {
    if (!quote) return;
    const id = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [quote]);

  useEffect(() => {
    if (countdown === 0 && quote) {
      setQuote(null);
      setAmount((a) => Number(a));
    }
  }, [countdown, quote]);

  const handleContinue = useCallback(() => {
    if (!quote) return;
    if (!user) { setAuthOpen(true); return; }
    onContinue?.({ quote, payment });
  }, [quote, user, payment, onContinue]);

  const fmtFn = useCallback((v) => cryptoFormat(v), []);

  return (
    <>
      <div className="space-y-5 pb-24 md:pb-0" data-testid="quote-screen">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">You pay</Label>
          <div className="flex items-stretch gap-2 bg-slate-50 border border-slate-200 rounded-2xl p-1.5 focus-within:border-accent transition-colors">
            <div className="flex items-center pl-3 font-display font-bold text-2xl text-primary">€</div>
            <Input
              type="text" inputMode="decimal" pattern="[0-9.]*"
              value={amount}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9.]/g, "");
                setAmount(v === "" ? "" : Number(v));
              }}
              className="flex-1 bg-transparent border-0 text-2xl font-display font-bold focus-visible:ring-0 focus-visible:ring-offset-0 px-1 h-12 tabular-nums"
              data-testid="fiat-amount-input"
            />
            <div className="text-xs font-semibold text-slate-500 pr-3 self-center">EUR</div>
          </div>
          <div className="flex gap-2 flex-wrap pt-1">
            {QUICK.map((v) => (
              <button
                key={v} onClick={() => setAmount(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors
                  ${Number(amount) === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`quick-amount-${v}`}
              >€{v}</button>
            ))}
            <span className="text-[11px] text-slate-400 self-center ml-auto">Min €20 · Max €25,000</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">You receive</Label>
          <div className="flex items-stretch gap-2 bg-white border border-slate-200 rounded-2xl p-2 focus-within:border-accent transition-colors">
            <div className="flex-1 px-2 self-center min-w-0">
              <div className="text-2xl font-display font-bold text-primary truncate" data-testid="receive-amount">
                {loading && !quote ? (
                  <Skeleton className="h-7 w-32 rounded" data-testid="receive-skeleton" />
                ) : (
                  <AnimatedNumber value={quote ? quote.receive_amount : 0} format={fmtFn} />
                )}
              </div>
              {quote ? (
                <div className="text-xs text-slate-500 font-mono">
                  1 {asset} ≈ {formatEUR(quote.quoted_rate)}
                </div>
              ) : loading ? (
                <Skeleton className="h-3 w-24 mt-1 rounded" />
              ) : null}
            </div>
            <AssetSelector value={asset} onChange={setAsset} prices={prices} />
          </div>
          {networks.length > 1 && (
            <Select value={network} onValueChange={setNetwork}>
              <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200" data-testid="network-select">
                <SelectValue placeholder="Network" />
              </SelectTrigger>
              <SelectContent>
                {networks.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Fee breakdown */}
        {quote && (
          <Collapsible open={feeOpen} onOpenChange={setFeeOpen}>
            <CollapsibleTrigger className="w-full flex items-center justify-between text-sm font-semibold text-slate-600 hover:text-primary transition-colors" data-testid="fee-breakdown-toggle">
              <span className="flex items-center gap-1.5"><Info className="w-4 h-4" /> Fee breakdown</span>
              <span className="flex items-center gap-1 font-mono text-xs">
                <Lock className="w-3 h-3" /> Rate locked · {countdown}s
                <ChevronDown className={`w-4 h-4 transition-transform ${feeOpen ? "rotate-180" : ""}`} />
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-1.5 text-sm bg-slate-50 rounded-xl p-3 font-mono" data-testid="fee-breakdown">
              <Row label="Mid-market rate" value={`1 ${asset} = ${formatEUR(quote.mid_rate)}`} />
              <Row label={`Spread (${quote.spread_bps / 100}%)`} value={formatEUR(quote.quoted_rate - quote.mid_rate)} />
              <Row label="Service fee (0.99%)" value={formatEUR(quote.service_fee)} />
              <Row label="Network fee" value={formatEUR(quote.network_fee)} />
              <div className="border-t border-slate-200 my-1.5" />
              <Row label="You receive" value={`${formatCrypto(quote.receive_amount)} ${asset}`} bold />
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Payment method */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment method</Label>
          <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 gap-2">
            <PaymentOpt value="sepa" current={payment} title="SEPA transfer" desc="0% fee · 1–2 hours" />
            <PaymentOpt value="card" current={payment} title="Card · Apple Pay" desc="Instant · 1.5%" />
          </RadioGroup>
        </div>

        {/* Desktop CTA */}
        <Button
          onClick={handleContinue} disabled={!quote || loading}
          className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-12 font-display font-semibold text-base hidden md:flex"
          data-testid="quote-continue-btn-desktop"
        >
          {loading ? "Getting quote…" : `Lock rate & continue → ${quote ? formatCrypto(quote.receive_amount) + " " + asset : ""}`}
        </Button>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hidden md:flex">
          <ShieldCheck className="w-3.5 h-3.5 text-primary" />
          Secure, compliance-first checkout
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-100 px-4 py-3 pb-[max(env(safe-area-inset-bottom),12px)]"
        data-testid="mobile-sticky-cta"
      >
        <Button
          onClick={handleContinue} disabled={!quote || loading}
          className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-12 font-display font-semibold text-base"
          data-testid="quote-continue-btn"
        >
          {loading ? "Getting quote…" : quote ? `Continue → ${formatCrypto(quote.receive_amount)} ${asset}` : "Enter amount"}
        </Button>
      </div>

      <AuthGate open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => onContinue?.({ quote, payment })} />
    </>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? "text-primary font-bold" : "text-slate-600"}`}>
    <span className="text-xs">{label}</span>
    <span className="text-xs tabular-nums">{value}</span>
  </div>
);

const PaymentOpt = ({ value, current, title, desc, icon }) => {
  const active = value === current;
  return (
    <label
      htmlFor={`pm-${value}`}
      className={`flex flex-col cursor-pointer rounded-2xl border-2 p-3 transition-all
        ${active ? "border-accent bg-accent/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
      data-testid={`payment-method-${value}`}
    >
      <RadioGroupItem value={value} id={`pm-${value}`} className="sr-only" />
      <div className="font-display font-bold text-sm text-primary flex items-center gap-1.5">
        {icon} {title}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
    </label>
  );
};
